import { resolvePregnancyStands } from "../pregnancy/resolvePregnancyStatus";
import type { PatientPregnancyStands } from "../pregnancy/useCyclePregnancyStatus";
/**
 * Read the ending off the cycle Home reports on.
 *
 * Extracted from `PlanHomeScreen` so the *wiring* is testable, not just the
 * resolver behind it. A unit test that hands `resolveHomeExperience` an outcome
 * directly cannot catch reading the wrong field off the cycle record — which is
 * exactly the bug this file was created to fix: verification was read from the
 * cycle when the outcome carries its own, so a refuted result was presented as
 * fact.
 *
 * Deliberately answers only "how did the last cycle end". The cycle type and
 * status the experience resolver receives stay with the caller, which also knows
 * about the active cycle.
 */

import type { Cycle, CycleOutcome } from "../../cycles/hooks/useCycles";
import type { HomeExperienceOutcomeInput } from "./resolveHomeExperience";

export type CyclePregnancyStandsById = Readonly<
  Record<string, PatientPregnancyStands | "unknown" | null>
>;

export type HomeCycleEnding = {
  patientPregnancyStands?: PatientPregnancyStands | "unknown" | null;
  outcome: HomeExperienceOutcomeInput | null;
  cycleId: string | null;
  outcomeId: string | null;
  outcomeType: CycleOutcome["type"] | null;
  acknowledgedAt: string | null;
  endDate: string | null;
  /** The ended cycle's own type, or null when there is no ended cycle. */
  endedCycleType: string | null;
  /** Set once a pregnancy reached a birth — it is no longer being followed up. */
  pregnancyOutcome: CycleOutcome["pregnancyOutcome"] | null;
  /** Set once a pregnancy was lost. */
  miscarriageOutcome: CycleOutcome["miscarriageOutcome"] | null;
  /**
   * Whether the record still stands. A row retracted as `entered_in_error` is
   * not a result, and Home must not be the reader that trusts it.
   *
   * No longer supplied by the cycle read: dashboard-api #1782 replaced the
   * outcome's `record_status` column with append-only lineage, and
   * `selectUnambiguousCurrentOutcome` now resolves `cycle.outcome` to the
   * current head server-side, so a retracted row does not reach this builder.
   * The rule is kept because it is this module's own, not the contract's — it
   * costs nothing and it is what stops a retraction ever being read as a
   * pregnancy to follow.
   */
  recordStatus?: string | null;
};

/**
 * A closed cycle is an arrival state only for a short period after its result
 * was recorded. Historical cycles should remain available in Care without
 * replacing the patient's current Home journey.
 */
export const CYCLE_ENDING_ARRIVAL_WINDOW_DAYS = 30;

/**
 * How long a recorded pregnancy can still be the patient's present.
 *
 * The arrival window above is wrong for a pregnancy: the cycle ends, the
 * pregnancy does not, and expiring it after a month took the follow-up off Home
 * while the person was still pregnant. But "never expires" cannot be right
 * either — "Add past cycle" and the fertility-treatments import create cycles
 * born closed, so a pregnancy from years ago would present itself as current.
 *
 * Term gestation is 280 days from the last period, and a result is recorded a
 * few weeks into that, so a pregnancy recorded here can plausibly still be
 * ongoing for around nine more months. 300 days covers that with slack.
 *
 * This is a backstop for historical records, not the lifetime of a live
 * follow-up: a birth, a loss, a correction, or a new cycle each end it sooner,
 * and once the follow-up state is server-backed it owns this precisely.
 */
export const PREGNANCY_FOLLOW_UP_WINDOW_DAYS = 300;

export const NO_CYCLE_ENDING: HomeCycleEnding = {
  outcome: null,
  cycleId: null,
  outcomeId: null,
  outcomeType: null,
  acknowledgedAt: null,
  endDate: null,
  endedCycleType: null,
  pregnancyOutcome: null,
  miscarriageOutcome: null,
};

/**
 * Most recently ended first, and a cycle that stored an end date before one that
 * did not.
 *
 * `endDate` is the only field that says when a cycle actually ended. `updatedAt`
 * and `createdAt` are stand-ins for a record that never stored one — and closed
 * cycles with no end date are not an edge case: `PastCycleEntryDialog` never
 * sends one, and `AddPastCycleDialog` leaves it optional. Sorting purely by the
 * best available date therefore let a history entry written today, whose
 * `updatedAt` is now, outrank every real ending. It then reported
 * `endDate: null`, which no window can measure, so adding a past cycle took the
 * closed-cycle Home away entirely — permanently, for a live pregnancy.
 */
function compareByEnding(a: Cycle, b: Cycle): number {
  const aIsDated = !!a.endDate;
  const bIsDated = !!b.endDate;
  if (aIsDated !== bIsDated) return aIsDated ? -1 : 1;

  const aDate = a.endDate ?? a.updatedAt ?? a.createdAt ?? "";
  const bDate = b.endDate ?? b.updatedAt ?? b.createdAt ?? "";
  return bDate.localeCompare(aDate);
}

/**
 * The ended cycle to report on: the most recent one, except that a pregnancy the
 * patient is still living stays the subject even when another cycle closed more
 * recently.
 *
 * The realistic way a newer close appears beside a live pregnancy is a backfilled
 * record — "Add past cycle" and the fertility-treatments import both create
 * cycles born closed — and entering one's own history must not evict the
 * follow-up. The preference is bounded by the same window the follow-up is, so an
 * imported pregnancy from years ago cannot outrank a genuinely recent ending
 * either.
 *
 * This is the plain answer, and it is what Care asks for: which cycle's guidance
 * applies. Home wants something narrower — see `homeSubjectClosedCycle`.
 */
export function latestClosedCycle(
  cycles: Cycle[] | undefined,
  now = Date.now(),
): Cycle | null {
  const closed = closedByEnding(cycles);
  return continuingPregnancyCycle(closed, now) ?? closed[0] ?? null;
}

function closedByEnding(cycles: Cycle[] | undefined): Cycle[] {
  return [...(cycles ?? [])]
    .filter((cycle) => cycle.status === "closed")
    .sort(compareByEnding);
}

/**
 * When an ending was recorded, for ordering.
 *
 * `endDate` is the fallback rather than the key: it has day resolution, so on its
 * own it cannot order two endings recorded hours apart.
 *
 * Be precise about what the fallback does, because it is not "sorts by its day":
 * a bare `2026-08-26` is a *prefix* of `2026-08-26T02:00:00Z`, so it sorts before
 * every timestamp of its own day and loses every same-day contest. That is
 * acceptable only because it is unreachable for anything in `readable` — the
 * generated schema types the outcome's `recordedAt` as a required, non-nullable
 * string. It is a guard against a record shape the contract does not allow, not a
 * supported ordering.
 */
function recordingOrder(cycle: Cycle): string {
  return cycle.outcome?.recordedAt ?? cycle.endDate ?? "";
}

function continuingPregnancyCycle(closed: Cycle[], now: number): Cycle | null {
  return (
    closed.find((cycle) => {
      const ending = readCycleEnding(cycle);
      return (
        isContinuingPregnancyEnding(ending) &&
        shouldUseCycleEndingForHomeExperience(ending, now)
      );
    }) ?? null
  );
}

/**
 * The ended cycle *Home* reports on, which is not always the one above.
 *
 * Closing a cycle is a deliberate, present-tense act, and while a pregnancy was
 * being followed it produced no response at all: Home skipped straight past the
 * cycle the patient had just closed, back to the pregnancy, and greeted her with
 * "Congratulations — it's positive". Closing a cycle with no result to report was
 * answered with congratulations for a different one.
 *
 * The rule that answers it is now the plainest one available: **the most recent
 * ending is the subject**, whatever its type and whether or not she has read it.
 * A continuing pregnancy takes Home when it *is* the most recent, which is the
 * case that matters clinically.
 *
 * Two bounds, both load-bearing:
 *
 * - **Ordering is by `recordedAt`, not `endDate`.** `endDate` is a date, not a
 *   timestamp, so same-day closes tie; `compareByEnding` returns 0 and a stable
 *   sort left the winner decided by the order the API happened to return.
 * - **A candidate has to clear `shouldUseCycleEndingForHomeExperience`**, so it
 *   must name a result and carry a real end date inside its window. That is what
 *   a backfilled record has to get past.
 *
 * Home only. Care keeps `latestClosedCycle`.
 */
/**
 * The ending Home reports on, and the pregnancy it may hand back to.
 *
 * Both answers come from one place because they have to agree. When the subject
 * was chosen by `recordedAt` and the pregnancy by `compareByEnding`, two distinct
 * unconcluded pregnancy records — a duplicate backfill is the realistic way to get
 * them — could be picked by different orderings, and
 * whether an ending stood in front of a pregnancy then flipped with the order the API
 * happened to return. That is the coin flip this module exists to remove, so the
 * two selections share a resolver rather than reproducing each other.
 */
function resolveHomeSubject(
  cycles: Cycle[] | undefined,
  now: number,
  pregnancyStandsById: CyclePregnancyStandsById = {},
): { subject: Cycle | null; continuingPregnancy: Cycle | null } {
  const closed = closedByEnding(cycles);

  /**
   * The endings she could actually read.
   *
   * A cycle closed with no outcome row, or one whose ending falls outside the
   * arrival window, is not an ending she could have read — so it must not decide
   * the ordering, and it must not be handed to Home as a subject either.
   */
  const readable = closed.filter((cycle) =>
    // Already refuses an ending with no outcome row, so that is not restated.
    shouldUseCycleEndingForHomeExperience(
      readCycleEnding(cycle, pregnancyStandsById[cycle.id]),
      now,
    ),
  );

  const mostRecentlyRecorded = (candidates: Cycle[]): Cycle | null =>
    [...candidates].sort((a, b) => {
      const byRecording = recordingOrder(b).localeCompare(recordingOrder(a));
      if (byRecording !== 0) return byRecording;
      // A genuine tie needs *some* deterministic answer, and the id is the only
      // stable one left. It is arbitrary in meaning but not in outcome, which is
      // the whole point: the alternative is the order the API happened to return.
      return (a.id ?? "").localeCompare(b.id ?? "");
    })[0] ?? null;

  const continuingPregnancy = mostRecentlyRecorded(
    readable.filter((cycle) =>
      isContinuingPregnancyEnding(
        readCycleEnding(cycle, pregnancyStandsById[cycle.id]),
      ),
    ),
  );

  /**
   * The ending she most recently *got*, and whether she has read it yet.
   *
   * Three attempts at this were too clever, and a real account killed each one.
   * The rule that survives is the plain one: order the endings she could read by
   * when they were recorded, look at the newest, and ask whether she has read it.
   *
   * Why `recordedAt` and not `endDate`: `endDate` is a date, not a timestamp, so
   * every cycle closed on the same day ties — `compareByEnding` returns 0 and a
   * stable sort leaves the winner to the order the API happened to return. Every
   * version of this that reasoned in days inherited that coin flip. `recordedAt`
   * is a `timestamp with time zone` on the outcome row and says when *this ending*
   * was recorded, which is the question being asked.
   *
   * Why the pregnancy is an ordinary candidate rather than a floor: bounding the
   * scan to endings strictly newer than the pregnancy meant a pregnancy closed the
   * same day could never be displaced. On the reporting account a positive was
   * recorded at 14:35 and read at 14:47, and a negative recorded at 14:47:31 —
   * after both — was excluded by that comparison, so closing a cycle on a beta of
   * 1 was still answered with "Félicitations, c'est positif".
   */
  const newest = mostRecentlyRecorded(readable);

  if (!newest) {
    // Preserve historical cycle context when no recent result is readable,
    // but never retain a pregnancy the patient has said has concluded.
    return {
      subject:
        closed.find(
          (cycle) =>
            !hasConcludedPregnancy(
              readCycleEnding(cycle, pregnancyStandsById[cycle.id]),
            ),
        ) ?? null,
      continuingPregnancy,
    };
  }

  /**
   * The newest ending is the subject, read or unread.
   *
   * This used to hand Home back to a continuing pregnancy once the newest ending
   * had been read, so that one "Got it" settled a whole day of endings instead of
   * asking for a tap each. The cost was invisible until the post-cycle home
   * existed: a patient who finished egg freezing, saw the ending and dismissed it
   * was handed back to a pregnancy recorded months earlier — whose outcome then
   * resolved to `positive`, short-circuited `deriveLifecycleStage` three lines
   * from the top, and greeted her with "Congratulations, it's positive" over a
   * cycle that stored eggs. Nothing in the app could get her out of it: the
   * pregnancy's window is 300 days and its eligibility ignores acknowledgement
   * entirely.
   *
   * The rule is now the plain one Anaïs asked for on 2026-09-01: **the most
   * recent cycle decides the home**, whatever its type. A continuing pregnancy
   * still takes Home whenever it *is* the most recent ending, which is the case
   * that matters clinically — someone currently pregnant recorded that pregnancy
   * after whatever came before it. Its long window now does only what it was
   * described as doing: keeping a pregnancy present while nothing newer exists.
   *
   * `continuingPregnancy` is still resolved and returned so callers can see that
   * a pregnancy stands behind a newer ending. Nothing reads it to decide whether
   * to offer an acknowledgement any more: under this rule acknowledging never
   * moves the subject, so no hero has a "Got it" worth pressing.
   */
  return { subject: newest, continuingPregnancy };
}

export function homeSubjectClosedCycle(
  cycles: Cycle[] | undefined,
  now = Date.now(),
  pregnancyStandsById: CyclePregnancyStandsById = {},
): Cycle | null {
  return resolveHomeSubject(cycles, now, pregnancyStandsById).subject;
}

export function readCycleEnding(
  cycle: Cycle | null,
  patientPregnancyStands?: PatientPregnancyStands | "unknown" | null,
): HomeCycleEnding {
  if (!cycle) return NO_CYCLE_ENDING;

  return {
    ...(patientPregnancyStands === undefined ? {} : { patientPregnancyStands }),
    outcome: cycle.outcome
      ? {
          type: cycle.outcome.type ?? null,
          resultStatus: cycle.outcome.resultStatus ?? null,
          // The outcome's own verification, not the cycle's. Both fields exist;
          // only this one says whether *this result* was refuted, and every
          // other reader in the codebase uses it.
          verificationStatus: cycle.outcome.verificationStatus ?? null,
        }
      : null,
    cycleId: cycle.id ?? null,
    outcomeId: cycle.outcome?.id ?? null,
    outcomeType: cycle.outcome?.type ?? null,
    acknowledgedAt: cycle.outcome?.acknowledgedAt ?? null,
    endDate: cycle.endDate ?? null,
    endedCycleType: cycle.cycleType ?? null,
    pregnancyOutcome: cycle.outcome?.pregnancyOutcome ?? null,
    miscarriageOutcome: cycle.outcome?.miscarriageOutcome ?? null,
    // Read structurally because the cycle contract no longer declares it:
    // dashboard-api #1782 replaced the outcome's `record_status` column with
    // append-only lineage, so a retracted row is a superseded row and
    // `cycle.outcome` already resolves to the current head. Nothing the API
    // sends today can populate this. It is forwarded rather than dropped so
    // the retraction rule below — and the case in
    // scripts/cycles/select-home-cycle-ending.test.ts that pins it — keeps
    // working on any caller that does have the field.
    recordStatus:
      (cycle.outcome as { recordStatus?: string | null } | null)
        ?.recordStatus ?? null,
  };
}

function daysSinceEnding(
  endDate: string | null | undefined,
  now: number,
): number | null {
  if (!endDate) return null;
  const endedMs = new Date(`${endDate}T00:00:00`).getTime();
  if (Number.isNaN(endedMs)) return null;
  const daysSince = (now - endedMs) / 86_400_000;
  return daysSince >= 0 ? daysSince : null;
}

export function isCycleEndingRecent(
  endDate: string | null | undefined,
  now = Date.now(),
): boolean {
  const daysSince = daysSinceEnding(endDate, now);
  return daysSince !== null && daysSince <= CYCLE_ENDING_ARRIVAL_WINDOW_DAYS;
}

/**
 * Which recorded result statuses describe a pregnancy there is still to follow.
 *
 * Exhaustive over the schema enum, and an allowlist rather than a denylist. A
 * denylist gets this backwards: every status nobody listed — including one the
 * backend adds after this file was last read — would be read as a live pregnancy
 * and hold an early-pregnancy Home on screen for 300 days. A `Record` keyed on
 * the enum makes a new member a build error here instead of a silent claim about
 * someone's pregnancy.
 *
 * `pending` is not a pregnancy to follow: the result has not come in, so nothing
 * yet says there is one to hold Home for. It keeps the arrival window like any
 * other ending.
 */
const PREGNANCY_CONTINUES_BY_RESULT_STATUS: Record<
  NonNullable<CycleOutcome["resultStatus"]>,
  boolean
> = {
  positive: true,
  verified_positive: true,
  pending: false,
  negative: false,
  verified_negative: false,
  uncertain: false,
  unavailable: false,
  cancelled: false,
  withdrawn: false,
  adverse: false,
};

/**
 * Widened to `string` on purpose. The status arrives as a loose string from the
 * boundary type, and a value this module has never heard of must fall outside
 * the set rather than be assumed into it.
 */
const CONTINUING_PREGNANCY_RESULT_STATUSES: ReadonlySet<string> = new Set(
  Object.entries(PREGNANCY_CONTINUES_BY_RESULT_STATUS)
    .filter(([, continues]) => continues)
    .map(([status]) => status),
);

/**
 * Whether the pregnancy this cycle recorded has since concluded.
 *
 * Separate from `isContinuingPregnancyEnding` because the two answer different
 * questions: that one asks whether to hold Home for a pregnancy, this one asks
 * whether Home may still describe one at all.
 */
export function hasConcludedPregnancy(
  ending: Pick<
    HomeCycleEnding,
    | "outcomeType"
    | "pregnancyOutcome"
    | "miscarriageOutcome"
    | "patientPregnancyStands"
  >,
): boolean {
  const stands = resolvePregnancyStands(ending, ending.patientPregnancyStands);
  return (
    ending.outcomeType === "pregnancy" &&
    (stands === "ended" || stands === "birth")
  );
}

/**
 * Whether this ending is a pregnancy the patient is still living.
 *
 * The cycle is over either way — that is what makes this necessary. A pregnancy
 * outlives the treatment that produced it, so it keeps the cycle as the thing
 * Home is about; a birth, a loss, a refutation, a retraction, or a status that
 * does not describe a pregnancy to follow each mean it does not.
 */
export function isContinuingPregnancyEnding(
  ending: Pick<
    HomeCycleEnding,
    | "outcomeId"
    | "outcomeType"
    | "outcome"
    | "pregnancyOutcome"
    | "miscarriageOutcome"
    | "recordStatus"
    | "patientPregnancyStands"
  >,
): boolean {
  if (ending.patientPregnancyStands === "unknown") return false;
  if (!ending.outcomeId) return false;
  if (ending.outcomeType !== "pregnancy") return false;
  if (ending.recordStatus === "entered_in_error") return false;
  if (hasConcludedPregnancy(ending)) return false;
  if (ending.outcome?.verificationStatus === "refuted") return false;

  const resultStatus = ending.outcome?.resultStatus ?? null;
  // No status at all is what the app's own write path leaves: `type:
  // "pregnancy"` with nothing qualifying it, which is a recorded pregnancy.
  return (
    resultStatus === null ||
    CONTINUING_PREGNANCY_RESULT_STATUSES.has(resultStatus)
  );
}

export function shouldUseCycleEndingForHomeExperience(
  ending: Pick<
    HomeCycleEnding,
    | "outcomeId"
    | "endDate"
    | "outcomeType"
    | "outcome"
    | "pregnancyOutcome"
    | "miscarriageOutcome"
    | "recordStatus"
    | "patientPregnancyStands"
  >,
  now = Date.now(),
): boolean {
  if (ending.patientPregnancyStands === "unknown") {
    // Preserve the existing result route during a failed/pending read. Its
    // status-dependent guidance and celebration remain independently withheld.
    return shouldUseCycleEndingForHomeExperience(
      { ...ending, patientPregnancyStands: null },
      now,
    );
  }
  if (!ending.outcomeId) return false;

  if (isContinuingPregnancyEnding(ending)) {
    const daysSince = daysSinceEnding(ending.endDate, now);
    return daysSince !== null && daysSince <= PREGNANCY_FOLLOW_UP_WINDOW_DAYS;
  }

  /**
   * A concluded pregnancy gets no window, not even the arrival one.
   *
   * The arrival window is measured from the cycle's `endDate`, and a terminator
   * does not restamp it — recording an ending leaves the cycle itself alone, so
   * a birth can be logged months later — so falling through to
   * recency only *looks* like it does something. Where it does fire it is worse
   * than doing nothing: the experience resolver is handed `type: "pregnancy"`
   * and nothing downstream reads `pregnancyOutcome`, so someone who has just
   * recorded a birth would be greeted as newly pregnant.
   *
   * So the truthful answer is to stop describing the pregnancy. Home has no
   * surface that acknowledges a birth, and inventing one is a product decision,
   * not a fix — until it exists this is a hard cut, deliberately rather than by
   * accident of a date.
   */
  if (hasConcludedPregnancy(ending)) return false;

  return isCycleEndingRecent(ending.endDate, now);
}

/**
 * Whether the ending is still being arrived at rather than rested in.
 *
 * The arrival carousel and the acknowledged pause are two presentations of one
 * ending and must never be on screen together. They are complementary on a single
 * server field: the carousel runs until the patient acknowledges, the pause begins
 * once they have. Expressing the carousel's gate here keeps that mutual exclusion
 * checkable, instead of leaving it as a coincidence of two separate conditions.
 */
export function isCycleClosingArrival(
  ending: Pick<HomeCycleEnding, "acknowledgedAt">,
): boolean {
  return !ending.acknowledgedAt;
}
